<script setup lang="ts">
export type PageInfo = {
  totalCount?: number
  totalPageCount?: number
  pageSize?: number
  currentPage?: number
  hasPrevious?: boolean
  hasNext?: boolean
}

const t2 = useT2Base({
  Next: 'Kitas',
})

const props = defineProps<{
  border?: boolean
  input: None | PageInfo
  skipnumbers?: boolean
  change(num: number): NotVoid
}>()

const mobile = useMobile()

function save(number: number): NotVoid {
  const num = helpers.toNumber(number)
  if (num > 0 && (props.input?.totalPageCount || 0) >= num) return props.change(num)
  return null
}
function back(): NotVoid {
  const input = props.input
  const value = input?.currentPage && input?.currentPage - 1
  if (input?.hasPrevious && value) return save(value)
  return null
}
function next(): NotVoid {
  const input = props.input
  const value = input?.currentPage && input?.currentPage + 1
  if (!(input?.hasNext && value && value <= (input?.totalPageCount || 0))) return null
  return save(value)
}

const constructValues = computed(() => {
  return mobile.value ? { showAll: 4, splitOn: 3, activeInMiddle: 1 } : { showAll: 8, splitOn: 6, activeInMiddle: 2 }
})

const constructedArr = computed(() => {
  const activeNumber = props.input?.currentPage || 1
  const max = props.input?.totalPageCount || 1
  const construct = constructValues.value

  if (max < construct.showAll) return [createArr(1, max)]
  if (activeNumber < construct.splitOn) return [createArr(1, construct.splitOn), [max]]
  if (activeNumber > max - construct.splitOn) return [[1], createArr(max - construct.splitOn, max)]

  const activeInMiddle = construct.activeInMiddle
  return [[1], createArr(activeNumber - activeInMiddle, activeNumber + activeInMiddle), [max]]
})

const fixedArrays = computed(() => {
  const activeNumber = props.input?.currentPage || 1
  const max = props.input?.totalPageCount || 1
  const activeItemsList = constructedArr.value.filter((arr) => arr.length > 0)

  const arrSizeIndex = activeItemsList.length - 1

  return helpers.arr(activeItemsList).map((i, index) => ({
    key: `${index}`,
    key2: `e${index}`,
    notLast: index !== arrSizeIndex,
    items: helpers.arr(i).map((number) => ({
      key: `${number}`,
      props: {
        number,
        max,
        activeNumber,
        setNumber: (val: number) => select(val),
      },
    })),
  }))
})

function select(number: number) {
  save(number)
}

function createArr(fromInp: number, toInp: number): number[] {
  const from = Number.parseInt(fromInp?.toString(), 10)
  const to = Number.parseInt(toInp?.toString(), 10)

  if (Number.isNaN(from) || Number.isNaN(to) || to <= from) return [1]
  const size = to - from + 1
  const arr: number[] = []

  for (let index = 0; index < size; index++) {
    arr[index] = from + index
  }
  return arr
}

const rightDisabled = computed(() => !props.input?.hasNext)
const rightLabel = computed(() => (rightDisabled.value || mobile.value ? undefined : ts(t2('Next'), (props.input?.currentPage || 1) + 1)))

const css = useCssModule()
const itemCss = computed(() => {
  const { border } = props
  return [css.item, border && css.border, mobile.value ? '_br9' : undefined]
})
</script>

<template>
  <div>
    <div :class="$style.cont" class="_stretch _hidden _wm100p m_ctrY m_ctrX _relative _row _gap2 d_h50 m_h60 m_w100p">
      <template v-if="!skipnumbers">
        <ButtonActionArrow
          v-if="!mobile"
          icon="left"
          :txt="undefined"
          :disabled="!input?.hasPrevious"
          :submit="() => back()"
          class="_svg"
          height="50"
          width="min50"
          :class="[$style.arr, $style.arr1]"
        />

        <div class="_relative _row _h100p">
          <template v-for="item in fixedArrays" :key="item.key">
            <div class="_row">
              <PaginationItem v-for="item2 in item.items" :key="item2.key" v-bind="item2.props" :class="itemCss" />
            </div>
            <div v-if="item.notLast" :key="item.key2" :class="$style.empty"> ... </div>
          </template>
        </div>
      </template>

      <ButtonActionArrow :class="$style.arr" color width="min50" height="50" :submit="() => next()" :disabled="rightDisabled" :txt="rightLabel" icon />
    </div>
  </div>
</template>

<style module>
.cont .arr {
  align-self: center;
}
.cont .arr1 {
  --svg: 50px;
  --border0: transparent;
}

.empty {
  composes: global(_row);
  composes: global(_w28);
  composes: global(_ctrX);
  composes: global(_pb08);
  composes: global(_txt_ctr);

  composes: global(_noselect);

  align-items: flex-end;
  cursor: none;
}
/*  */
/*  */
/*  */

.item {
  composes: global(_relative);
  composes: global(d_fw-xl);
  composes: global(m_fw-l);
  composes: global(_fs09);
  composes: global(_h100p);
  composes: global(m_mr04);
  composes: global(_row);
  composes: global(_ctrY);
  composes: global(_ctrX);

  composes: global(_pr02);
  composes: global(_pb02);
  composes: global(_pl04);
  composes: global(_pr04);
  composes: global(_hidden);

  width: 46px;
  composes: global(_c1);
  cursor: pointer;

  background-color: transparent;
  border: solid transparent;
  border-width: 1px 0 2px;

  outline: none;
  z-index: 1;
  appearance: button;
  margin: 0 1px;
}
.item:global(.on) {
  background: var(--black);
  color: var(--bg);
}

.item:global(.edit) {
  opacity: 0.99;
  color: var(--black);
  border-bottom-color: var(--black);
  cursor: text;
}
.item :global(.input) {
  border: 0;
  outline: none;
  color: var(--black);
}
:global(.d) .item:hover:not(:global(.on)) {
  opacity: 0.99;
  color: var(--black);
  background: var(--black-5);
  text-decoration: 2px underline;
}

:global(.d) .item:global(.on):hover,
:global(.d) .item:global(.edit) {
  border-width: 1px 1px 2px;
  border-color: var(--black);
}

:global(.m) .item {
  background: hsl(240deg, 10%, 97%);
}
:global(.m) .item:last-child {
  margin-right: 0;
}

:global(.m) .item:not(.border):global(.on),
:global(.m) .item:global(.edit) {
  background: var(--black);
  color: var(--bg);
  border-width: 1px;
  font-weight: var(--fw-xl);
}

.border.item:global(.on) {
  background: var(--bg);
  color: var(--black);
  background: var(--bg);
  border: 1px solid var(--black);
}
</style>
